/**
 * SAP credit group codes (from the customer master / credit segment).
 *
 * Authored as name -> code so call sites read `CREDIT_GROUPS.e_commerce` instead of the opaque
 * `'CA7'` magic string. The code is what SAP sends and what gets persisted; the name is the
 * developer-facing handle and doubles as the i18n key (translate it under the `creditGroup` block),
 * so the UI shows "E-commerce" / "Commerce électronique" and no one ever authors or translates
 * `CA7`.
 */
export const CREDIT_GROUPS = {
  independant: 'CA1',
  key_account: 'CA2',
  wholesaler_4pl: 'CA3',
  duty_free: 'CA4',
  pharmacy: 'CA5',
  vape_shop: 'CA6',
  e_commerce: 'CA7',
  interco: 'CA8',
  non_trade_customer: 'CA9',
} as const

export type CreditGroupName = keyof typeof CREDIT_GROUPS
export type CreditGroupCode = (typeof CREDIT_GROUPS)[CreditGroupName]

export const CREDIT_GROUP_NAMES = Object.keys(CREDIT_GROUPS) as CreditGroupName[]
export const CREDIT_GROUP_CODES = Object.values(CREDIT_GROUPS) as CreditGroupCode[]

export const CREDIT_GROUP_NAME_BY_CODE = Object.fromEntries(
  Object.entries(CREDIT_GROUPS).map(([name, code]) => [code, name]),
) as Record<CreditGroupCode, CreditGroupName>

export function isCreditGroupCode(value: string | null | undefined): value is CreditGroupCode {
  return value != null && CREDIT_GROUP_NAME_BY_CODE[value as CreditGroupCode] !== undefined
}

export function getCreditGroupName(code: string): CreditGroupName | string {
  return CREDIT_GROUP_NAME_BY_CODE[code as CreditGroupCode] ?? code
}
